import React, { useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Heart, Shield, BookOpen, Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { aboutUsContent as content } from '../content/marketingPages';

/* ── Fade-in observer hook ─────────────────────────────── */
const useFadeIn = () => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { el.classList.add('about-fade-visible'); observer.unobserve(el); } },
            { threshold: 0.12 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return ref;
};

const FadeIn = ({ children, className = '', delay = 0 }) => {
    const ref = useFadeIn();
    return (
        <div ref={ref} className={`about-fade-hidden ${className}`} style={{ transitionDelay: `${delay}ms` }}>
            {children}
        </div>
    );
};

/* ── Color tokens ── */
const t = (isDark) => ({
    pageBg:        isDark ? '#171717' : '#ffffff',
    sectionAltBg:  isDark ? '#1c1c1c' : '#fafafa',
    cardBg:        isDark ? '#242424' : '#ffffff',
    cardBorder:    isDark ? '#333'    : '#eee',
    navBg:         isDark ? 'rgba(23,23,23,0.92)' : 'rgba(255,255,255,0.8)',
    navBorder:     isDark ? '#2a2a2a' : '#f5f5f7',
    textPrimary:   isDark ? '#ededed' : '#1d1d1f',
    textSecondary: isDark ? '#999'    : '#666',
    textMuted:     isDark ? '#666'    : '#999',
    footerText:    isDark ? '#555'    : '#ccc',
});

/* ── Section icons for visual variety ── */
const sectionIcons = [
    <Heart size={28} className="text-[#FFC107]" />,
    <BookOpen size={28} className="text-[#FFC107]" />,
    <Users size={28} className="text-[#FFC107]" />,
    <Shield size={28} className="text-[#FFC107]" />,
];

const AboutUsPage = ({ onBack, onGetStarted }) => {
    const { isDark } = useTheme();
    const c = t(isDark);

    return (
        <div style={{ backgroundColor: c.pageBg, color: c.textPrimary, minHeight: '100vh', fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>

            {/* ── Animation styles ── */}
            <style>{`
                .about-fade-hidden {
                    opacity: 0;
                    transform: translateY(20px);
                    transition: opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                                transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }
                .about-fade-visible {
                    opacity: 1;
                    transform: translateY(0);
                }
                .about-card-hover {
                    transition: transform 0.25s ease, box-shadow 0.25s ease;
                }
                .about-card-hover:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 40px rgba(0,0,0,0.08);
                }
            `}</style>

            {/* ── Nav ── */}
            <nav
                style={{
                    position: 'sticky', top: 0, zIndex: 50,
                    backgroundColor: c.navBg, borderBottom: `1px solid ${c.navBorder}`,
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                }}
            >
                <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                        onClick={onBack}
                        id="about-us-back-btn"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 14, color: c.textSecondary, background: 'none', border: 'none', cursor: 'pointer',
                            transition: 'color 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = c.textPrimary}
                        onMouseLeave={e => e.currentTarget.style.color = c.textSecondary}
                    >
                        <ArrowLeft size={16} />
                        Back
                    </button>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Heart size={18} className="text-[#FFC107]" />
                        <span style={{ fontWeight: 600, fontSize: 14, color: c.textPrimary }}>About Us</span>
                    </div>
                </div>
            </nav>

            {/* ═══════════════════════════════════════════ */}
            {/* ── Hero Section ── */}
            {/* ═══════════════════════════════════════════ */}
            <header style={{ padding: '120px 24px 80px', textAlign: 'center' }}>
                <FadeIn>
                    <h1 style={{
                        fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1,
                        marginBottom: 20,
                        color: c.textPrimary,
                    }}>
                        {content.hero.headline}
                    </h1>
                </FadeIn>
                <FadeIn delay={150}>
                    <p style={{
                        fontSize: 'clamp(1.05rem, 2vw, 1.3rem)',
                        lineHeight: 1.65,
                        color: c.textSecondary,
                        maxWidth: 600,
                        margin: '0 auto',
                    }}>
                        {content.hero.subheadline}
                    </p>
                </FadeIn>
            </header>

            {/* ═══════════════════════════════════════════ */}
            {/* ── Content Sections ── */}
            {/* ═══════════════════════════════════════════ */}
            {content.sections.map((section, i) => (
                <section
                    key={i}
                    style={{
                        backgroundColor: i % 2 === 1 ? c.sectionAltBg : c.pageBg,
                        padding: '80px 24px',
                    }}
                >
                    <div style={{ maxWidth: 680, margin: '0 auto' }}>
                        <FadeIn delay={i * 80}>
                            <div
                                className="about-card-hover"
                                style={{
                                    backgroundColor: c.cardBg,
                                    border: `1px solid ${c.cardBorder}`,
                                    borderRadius: 16,
                                    padding: '40px 36px',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                                    {sectionIcons[i % sectionIcons.length]}
                                    <h2 style={{
                                        fontSize: 'clamp(1.25rem, 2.5vw, 1.6rem)',
                                        fontWeight: 700,
                                        letterSpacing: '-0.01em',
                                        color: c.textPrimary,
                                        margin: 0,
                                    }}>
                                        {section.heading}
                                    </h2>
                                </div>
                                <p style={{
                                    fontSize: '1.05rem',
                                    lineHeight: 1.75,
                                    color: c.textSecondary,
                                    margin: 0,
                                }}>
                                    {section.body}
                                </p>
                            </div>
                        </FadeIn>
                    </div>
                </section>
            ))}

            {/* ═══════════════════════════════════════════ */}
            {/* ── CTA Section ── */}
            {/* ═══════════════════════════════════════════ */}
            <section style={{ background: '#FFC107', padding: '100px 24px', textAlign: 'center' }}>
                <FadeIn>
                    <h2 style={{
                        fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        color: '#fff',
                        marginBottom: 16,
                    }}>
                        {content.cta.text}
                    </h2>
                </FadeIn>
                <FadeIn delay={100}>
                    <button
                        onClick={onGetStarted}
                        id="about-us-cta-btn"
                        style={{
                            background: '#fff',
                            color: '#1d1d1f',
                            padding: '14px 32px',
                            borderRadius: 12,
                            fontSize: 16,
                            fontWeight: 600,
                            border: 'none',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                        {content.cta.button_label}
                        <ArrowRight size={18} />
                    </button>
                </FadeIn>
            </section>

            {/* ── Footer ── */}
            <footer style={{ padding: '48px 24px', textAlign: 'center', fontSize: 12, letterSpacing: '0.04em', color: c.footerText, backgroundColor: c.pageBg }}>
                <p>&copy; 2026 Saige</p>
            </footer>
        </div>
    );
};

export default AboutUsPage;
